import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useHistory, useLocation } from 'react-router-dom';

import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { downloadGHAAggregateReportCSV, getGHAAggregateReport } from '~/services/ConnectionGoogleService';

const columns: Array<GridColDef> = [
  { field: 'property_id', headerName: 'Property ID', width: 150, sortable: false },
  { field: 'hotel_name', headerName: 'Hotel Name', width: 200, sortable: false },
  { field: 'earliest_crawl_datetime', headerName: 'Earliest Crawl Time', width: 200, sortable: false },
  { field: 'latest_crawl_datetime', headerName: 'Latest Crawl Time', width: 200, sortable: false },
  { field: 'avg_no_of_paid_results', headerName: 'Avg Paid Results', width: 150, sortable: false },
  { field: 'median_no_of_paid_results', headerName: 'Median Paid Results', width: 150, sortable: false },
  { field: 'avg_lowest_price_paid_results', headerName: 'Avg Lowest Price (Paid)', width: 200, sortable: false },
  {
    field: 'median_lowest_price_paid_results',
    headerName: 'Median Lowest Price (Paid)',
    width: 200,
    sortable: false,
  },
  { field: 'avg_highest_price_paid_results', headerName: 'Avg Highest Price (Paid)', width: 200, sortable: false },
  {
    field: 'median_highest_price_paid_results',
    headerName: 'Median Highest Price (Paid)',
    width: 200,
    sortable: false,
  },
  { field: 'avg_no_of_free_results', headerName: 'Avg Free Results', width: 150, sortable: false },
  { field: 'median_no_of_free_results', headerName: 'Median Free Results', width: 150, sortable: false },
  { field: 'avg_lowest_price_free_results', headerName: 'Avg Lowest Price (Free)', width: 200, sortable: false },
  {
    field: 'median_lowest_price_free_results',
    headerName: 'Median Lowest Price (Free)',
    width: 200,
    sortable: false,
  },
  { field: 'avg_highest_price_free_results', headerName: 'Avg Highest Price (Free)', width: 200, sortable: false },
  {
    field: 'median_highest_price_free_results',
    headerName: 'Median Highest Price (Free)',
    width: 200,
    sortable: false,
  },
  { field: 'avg_luxuryescapes_price', headerName: 'Avg LE Price', width: 150, sortable: false },
  { field: 'median_luxuryescapes_price', headerName: 'Median LE Price', width: 150, sortable: false },
  { field: 'avg_luxuryescapes_position', headerName: 'Avg LE Position', width: 150, sortable: false },
  { field: 'avg_le_margin', headerName: 'Avg LE Margin', width: 150, sortable: false },
  { field: 'median_le_margin', headerName: 'Median LE Margin', width: 150, sortable: false },
  { field: 'median_luxuryescapes_position', headerName: 'Median LE Position', width: 150, sortable: false },
  { field: 'time_in_paid_results', headerName: 'Time in Paid Results', width: 150, sortable: false },
  { field: 'percentage_within_paid_results', headerName: '% in Paid Results', width: 150, sortable: false },
  {
    field: 'avg_price_delta_le_vs_lowest_free',
    headerName: 'Avg Price Delta (LE vs Lowest Free)',
    width: 250,
    sortable: false,
  },
  {
    field: 'median_price_delta_le_vs_lowest_free',
    headerName: 'Median Price Delta (LE vs Lowest Free)',
    width: 250,
    sortable: false,
  },
  {
    field: 'avg_price_delta_le_vs_lowest_free_in_percentage',
    headerName: 'Avg Price Delta % (LE vs Lowest Free)',
    width: 250,
    sortable: false,
  },
  {
    field: 'median_price_delta_le_vs_lowest_free_in_percentage',
    headerName: 'Median Price Delta % (LE vs Lowest Free)',
    width: 250,
    sortable: false,
  },
];

const sortByOptions = [
  { value: 'percentage_within_paid_results', label: '% in Paid Results' },
  { value: 'median_price_delta_le_vs_lowest_free', label: 'Median Price Delta (LE vs Lowest Free)' },
  { value: 'avg_price_delta_le_vs_lowest_free', label: 'Avg Price Delta (LE vs Lowest Free)' },
];

const sortOrderOptions = [
  { value: 'asc', label: 'Ascending' },
  { value: 'desc', label: 'Descending' },
];

const aggregateNumberOptions = [3, 5, 10, 20];

function GHAPriceAggregatedReports() {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState([]);
  const [fetchingState, setFetchingState] = useState<Utils.FetchingState>('idle');

  const history = useHistory();
  const location = useLocation();

  const { page, pageSize, sortBy, sortOrder, aggregateNumber } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      page: parseInt(searchParams.get('page') || '0', 10),
      pageSize: parseInt(searchParams.get('pageSize') || '10', 10),
      sortBy: searchParams.get('sortBy') || 'median_price_delta_le_vs_lowest_free',
      sortOrder: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'asc',
      aggregateNumber: parseInt(searchParams.get('aggregateNumber') || '5', 10),
    };
  }, [location.search]);

  const [uiSortBy, setUiSortBy] = useState(sortBy);
  const [uiSortOrder, setUiSortOrder] = useState<'asc' | 'desc'>(sortOrder);
  const [uiAggregateNumber, setUiAggregateNumber] = useState(aggregateNumber);

  useEffect(() => {
    setUiSortBy(sortBy);
    setUiSortOrder(sortOrder);
    setUiAggregateNumber(aggregateNumber);
  }, [sortBy, sortOrder, aggregateNumber]);

  const [rowCount, setRowCount] = useState(0);

  const paginationModel = useMemo(() => {
    return {
      page: page,
      pageSize: pageSize,
    };
  }, [page, pageSize]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchData = useCallback(
    async (newPaginationModel?: GridPaginationModel) => {
      setFetchingState('loading');
      const modelToUse = newPaginationModel || paginationModel;
      try {
        const result = await getGHAAggregateReport({
          sortBy: sortBy,
          sortOrder: sortOrder,
          aggregateNumber: aggregateNumber,
          page: modelToUse.page,
          limit: modelToUse.pageSize,
        });

        setData(result?.result || []);
        setRowCount(result?.count || 0);
        setFetchingState('success');
      } catch (error) {
        setFetchingState('failed');
        enqueueSnackbar('GHA aggregated pricing record fetch failed', { variant: 'error' });
      }
    },
    [enqueueSnackbar, sortBy, sortOrder, aggregateNumber, paginationModel],
  );

  const handleSearch = () => {
    const params = new URLSearchParams();
    params.set('page', '0');
    params.set('pageSize', String(paginationModel.pageSize));
    params.set('sortBy', uiSortBy);
    params.set('sortOrder', uiSortOrder);
    params.set('aggregateNumber', String(uiAggregateNumber));
    history.push({ search: params.toString() });
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    const params = new URLSearchParams(location.search);
    params.set('page', String(model.page));
    params.set('pageSize', String(model.pageSize));
    params.set('sortBy', sortBy);
    params.set('sortOrder', sortOrder);
    params.set('aggregateNumber', String(aggregateNumber));
    history.push({ search: params.toString() });
  };

  const handleDownload = async () => {
    try {
      const blob = await downloadGHAAggregateReportCSV({
        aggregateNumber: uiAggregateNumber,
        sortBy: uiSortBy,
        sortOrder: uiSortOrder,
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `gha-aggregate-report-${uiAggregateNumber}-${uiSortBy}-${uiSortOrder}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      enqueueSnackbar('CSV file downloaded successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to download CSV file', { variant: 'error' });
    }
  };

  return (
    <Box mt={2}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <Autocomplete
          options={aggregateNumberOptions}
          value={uiAggregateNumber}
          onChange={(event, newValue) => {
            if (newValue !== null) {
              setUiAggregateNumber(newValue);
            }
          }}
          renderInput={(params) => <TextField {...params} label="Aggregate Number" />}
          sx={{ width: 200 }}
          disableClearable
        />
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel id="sort-by-label">Sort by</InputLabel>
          <Select
            labelId="sort-by-label"
            value={uiSortBy}
            label="Sort by"
            onChange={(e: SelectChangeEvent<string>) => setUiSortBy(e.target.value)}
          >
            {sortByOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="sort-order-label">Sort order</InputLabel>
          <Select
            labelId="sort-order-label"
            value={uiSortOrder}
            label="Sort order"
            onChange={(e: SelectChangeEvent<'asc' | 'desc'>) => setUiSortOrder(e.target.value as 'asc' | 'desc')}
          >
            {sortOrderOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
        <Button variant="outlined" onClick={handleDownload}>
          Download
        </Button>
      </Box>
      <DataGrid
        autoHeight
        rows={data}
        columns={columns}
        loading={fetchingState === 'loading'}
        getRowId={(row) => row.property_id}
        pageSizeOptions={[10, 50, 100, 250, 500]}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        paginationMode="server"
        rowCount={rowCount}
        slots={{ pagination: GridPagination }}
      />
    </Box>
  );
}

export default GHAPriceAggregatedReports;
